/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-09 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { Page, PageActions, Layout, Tabs, Card } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import { reduxForm, reset } from 'redux-form';

import GeneralTab from './general-tab';
import ProductItemTab from './product-item-tab';
import DidYouKnow from 'src/components/views/default-theme-settings/did-you-know';
import MainMenu from 'src/components/containers/main-menu';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';

import { URL } from 'src/constants/links';
import {
  buildInitialDefaultThemeSettingsValues,
  buildDefaultThemeSettingsValues
} from 'src/helpers/default-theme-settings-helper';
import * as defaultThemeSettingsActions from 'actions/default-theme-settings-actions';
import * as fontActions from 'actions/font-actions';
import * as filterDataActions from 'actions/filter-data-actions';
import TitleHelp from 'src/components/views/common/title-help';

import type {
  DefaultThemeSettingsType,
  DefaultThemeSettingsDisplayType
} from 'src/models/default-theme-settings';

type Props = WithI18nProps;

type State = {
  selected: number,
  youtubeModal: boolean
};

class DefaultThemeSettings extends Component<Props, State> {
  state = {
    selected: 0,
    youtubeModal: false
  };

  componentDidMount() {
    const { getDefaultThemeSettings, fontListSuccess, getFonts, getFilterData } = this.props;

    if (!fontListSuccess) {
      getFonts();
    }

    getFilterData();
    getDefaultThemeSettings();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.loadingDefaultThemeSettings && !this.props.loadingDefaultThemeSettings) {
      startSABLoading();
    }

    if (!nextProps.loadingDefaultThemeSettings && this.props.loadingDefaultThemeSettings) {
      stopSABLoading();
    }
  }

  render() {
    const { submitting, handleSubmit, pristine, savingDefaultThemeSettings, i18n } = this.props;
    const { selected, youtubeModal } = this.state;

    const tabs = [
      {
        id: 'general',
        content: i18n.translate('AdminHTML.DefaultThemeSettings.DefaultThemeSettings.tab1_Content'),
        panelID: 'general-tab'
      },
      {
        id: 'product-item',
        content: i18n.translate('AdminHTML.DefaultThemeSettings.DefaultThemeSettings.tab2_Content'),
        panelID: 'product-item-tab'
      }
    ];

    const tabComponents = [<GeneralTab />, <ProductItemTab />];

    return (
      <div className="BC-Page BC-Default-Theme-Settings">
        <MainMenu menuId="menu-theme-default-theme-settings" />
        <Page
          title={i18n.translate('AdminHTML.DefaultThemeSettings.DefaultThemeSettings.pageTitle')}
        >
          <TitleHelp
            i18n={i18n}
            onPlay={this.handleOpenYoutubeModal}
            hrefUserManual={URL['Link_V2_Theme_DefaultTheme_ManualGuide']}
          />
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                <DidYouKnow />
                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>

            {/* Tabs */}
            <Layout.Section>
              <form onSubmit={this.handleDefaultSubmit}>
                <Tabs tabs={tabs} selected={selected} onSelect={this.handleTabChange}>
                  <div style={{ marginTop: '1.5rem' }}>
                    <Card>{tabComponents[selected]}</Card>
                  </div>
                </Tabs>
              </form>
            </Layout.Section>
          </Layout>
          <PageActions
            primaryAction={{
              content: i18n.translate('AdminHTML.Common.Action.saveSettings'),
              onAction: handleSubmit(this.handleSave.bind(this)),
              disabled: pristine || submitting,
              loading: savingDefaultThemeSettings && !pristine
            }}
            secondaryActions={
              !pristine
                ? [
                    {
                      content: i18n.translate('AdminHTML.Common.Action.cancel'),
                      onAction: this.discardChange
                    }
                  ]
                : []
            }
          />
        </Page>
        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['Link_V2_Theme_DefaultTheme_Video']}
          title={i18n.translate(
            'AdminHTML.DefaultThemeSettings.DefaultThemeSettings.youtubeModal_Title'
          )}
          onClose={this.handleCloseYoutubeModal}
        />
      </div>
    );
  }
  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  // Handle change tab
  handleTabChange = (selectedTabIndex: number) => {
    this.setState({ selected: selectedTabIndex });
  };

  // Handle click save button
  handleSave = (data: DefaultThemeSettingsDisplayType) => {
    const { updateDefaultThemeSettings } = this.props;

    const files = [];
    const saleLabelImg = _.get(data, 'products.saleLabelImg', '');
    const soldOutImg = _.get(data, 'products.soldOutImg', '');

    // Extract imported files
    if (saleLabelImg && _.isObject(saleLabelImg)) {
      files.push(saleLabelImg);
    }
    if (soldOutImg && _.isObject(soldOutImg)) {
      files.push(soldOutImg);
    }

    // Build data to be saved
    const saveData = buildDefaultThemeSettingsValues(data);

    // Save settings
    updateDefaultThemeSettings(saveData, files);
  };

  discardChange = () => {
    const { resetForm } = this.props;

    resetForm();
  };

  handleOpenYoutubeModal = () => this.setState({ youtubeModal: true });
  handleCloseYoutubeModal = () => this.setState({ youtubeModal: false });
}

const DefaultThemeSettingsForm = reduxForm({
  form: 'DefaultThemeSettingsForm', // a unique identifier for this form
  enableReinitialize: true
})(DefaultThemeSettings);

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFilterData: () => dispatch(filterDataActions.getFilterData(ownProps.i18n)),
  getFonts: () => dispatch(fontActions.getFonts(ownProps.i18n)),
  getDefaultThemeSettings: () =>
    dispatch(defaultThemeSettingsActions.getDefaultThemeSettings(ownProps.i18n)),
  updateDefaultThemeSettings: (
    defaultThemeSettings: DefaultThemeSettingsType,
    files: Array<Object>
  ) =>
    dispatch(
      defaultThemeSettingsActions.updateDefaultThemeSettings(
        defaultThemeSettings,
        files,
        ownProps.i18n
      )
    ),
  resetForm: () => dispatch(reset('DefaultThemeSettingsForm'))
});

const mapStateToProps = (state, props) => {
  const { defaultThemeSettings } = state;
  return {
    fontListSuccess: _.get(state, 'data.fontListSuccess', false),
    defaultThemeSettings: _.get(defaultThemeSettings, 'defaultThemeSettings', {}),
    loadingDefaultThemeSettings: _.get(defaultThemeSettings, 'loadingDefaultThemeSettings', false),
    savingDefaultThemeSettings: _.get(defaultThemeSettings, 'savingDefaultThemeSettings', false),
    initialValues: buildInitialDefaultThemeSettingsValues(defaultThemeSettings.defaultThemeSettings)
  };
};
export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(DefaultThemeSettingsForm));
